import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { supabase } from "@/lib/supabase"

import type { TablesUpdate } from "../../database.types"

export interface UpdateApplicantPayload {
  id: string
  data: TablesUpdate<"applicants">
}

export const useUpdateApplicant = (applicationId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: UpdateApplicantPayload) => {
      // First try without the select to avoid 406 error
      const { error: updateError } = await supabase.from("applicants").update(data).eq("id", id)

      if (updateError) {
        console.error("Error updating applicant:", updateError)
        throw new Error(updateError.message)
      }

      // Then fetch the updated data separately
      const { data: updatedData, error: fetchError } = await supabase
        .from("applicants")
        .select("*")
        .eq("id", id)
        .single()

      if (fetchError) {
        console.error("Error fetching updated applicant:", fetchError)
        throw new Error(fetchError.message)
      }

      return updatedData
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applicationDetails", applicationId] })
      queryClient.invalidateQueries({ queryKey: ["applications"] })

      toast.success("Applicant Updated", {
        description: "Applicant details have been updated successfully",
      })
    },
    onError: (error: Error) => {
      toast.error("Update Failed", {
        description: error.message || "Failed to update applicant details",
      })
    },
  })
}
